import { Queue } from './queue'
import {
  observeMedia,
  ObserverMediaEventType,
  NativeMediaErrorCode,
  type ObserverMediaEvent,
} from './media_observer'
import { postHttp } from './http'
import { startTicker } from './time'
import { uuidv7 } from 'uuidv7'
import { now } from './date'
import { isTouchDevice } from '../../../utils/Devices'

export interface BootstrapOptions {
  enabled: boolean
  baseUrl: string
  batchReportInterval?: number
}

const KEEP_ALIVE_EVENT_TYPE = 0

enum SessionClosedEventReason {
  NONE = 0,
  SRC_CHANGE = 1,
  REPLAY = 2,
  EXIT = 3,
}

export type BaseMediaEvent = Omit<ObserverMediaEvent, 'type'> & {
  type: ObserverMediaEvent['type'] | typeof KEEP_ALIVE_EVENT_TYPE
  watchTime: number
  reason: SessionClosedEventReason
  playbackId: string
  mediaId: string
}

export type MediaEvent = BaseMediaEvent & {
  sequenceNumber: number
}

const initialSequenceNumber = 1

export class Agent {
  public observing: boolean
  public options: BootstrapOptions
  public isLoadStarted: boolean
  public isMobile: boolean
  public isHidden: boolean
  public mediaId: string
  public nextMediaId: string | null
  public playbackId: string
  public watchTime: number
  public sequenceNumber: number
  public lastVideoTime: number
  public lastWaitingEventReceived: BaseMediaEvent | null
  public lastEmptiedEventReceived: BaseMediaEvent | null
  public lastEndedEventReceived: BaseMediaEvent | null
  public lastEventSent: BaseMediaEvent | null
  public queue: Queue
  public stopObserver: (() => void) | null
  public stopTicker: (() => void) | null

  constructor(options: BootstrapOptions) {
    this.options = options
    this.observing = false
    this.isLoadStarted = false
    this.isMobile = isTouchDevice()
    this.isHidden = false
    this.mediaId = ''
    this.nextMediaId = null
    this.playbackId = ''
    this.watchTime = 0
    this.sequenceNumber = initialSequenceNumber
    this.lastVideoTime = 0
    this.lastWaitingEventReceived = null
    this.lastEmptiedEventReceived = null
    this.lastEndedEventReceived = null
    this.lastEventSent = null
    this.queue = new Queue(1000)

    this.stopObserver = () => undefined
    this.stopTicker = () => undefined
  }

  public observe(media: HTMLVideoElement, mediaId: string) {
    if (!this.options.enabled) return

    if (this.observing) {
      this.dispose()
    }

    this.observing = true

    this.mediaId = mediaId

    this.playbackId = uuidv7()

    this.stopObserver = observeMedia(media, (event) => {
      this.handleMediaEvents(event)
    })

    this.stopTicker = startTicker(() => {
      if (!(this.isHidden && this.isMobile)) {
        if (this.queue.getLength() > 0) {
          this.lastEventSent = this.queue.peekLast() || null
          this.report(this.queue.dequeue(), false)
        } else {
          this.report(
            [
              {
                ...(this.lastEventSent ?? {
                  currentTime: 0,
                  videoWidth: 0,
                  videoHeight: 0,
                  paused: false,
                  playbackRate: 1,
                  watchTime: 0,
                  playbackId: this.playbackId,
                  mediaId: this.mediaId,
                }),
                type: KEEP_ALIVE_EVENT_TYPE,
                errorCode: NativeMediaErrorCode.NONE,
                reason: SessionClosedEventReason.NONE,
                emittedAt: now(),
                sequenceNumber: this.sequenceNumber,
                playbackId: this.playbackId,
              },
            ],
            false,
          )
          this.sequenceNumber++
        }
      }
    }, this.options.batchReportInterval!)
  }

  public handleMediaEvents(event: ObserverMediaEvent) {
    // ---- 0.HANDLE THE CASE PLAYER IS VISIBLE AGAIN AND START A NEW PLAYBACK ON MOBILE IN THIS CASE ---- //
    if (
      event.type === ObserverMediaEventType.VISIBILITY_CHANGE &&
      this.isHidden
    ) {
      this.isHidden = false
      if (this.isMobile) {
        this.startNewPlayback(false)
      }
      return
    }
    // Do not log "parasite" events while media loading is not started or while player is hidden on mobile
    if (
      (this.isLoadStarted ||
        event.type === ObserverMediaEventType.LOAD_START) &&
      !(this.isHidden && this.isMobile)
    ) {
      // ---- 1.HANDLE COMPUTED WATCH TIME PROPERTY ---- //

      //// 1.1. Start to handle events and Reset Watch Time
      if (event.type === ObserverMediaEventType.LOAD_START) {
        this.isLoadStarted = true
        this.watchTime = 0
      }

      //// 1.2. Add some watched time when time updates
      if (event.type === ObserverMediaEventType.TIME_UPDATE) {
        if (this.lastVideoTime < event.currentTime && event.playbackRate > 0) {
          this.watchTime +=
            (event.currentTime - this.lastVideoTime) / event.playbackRate
        }
      }

      //// 1.3. Save current time to compute watch time on next time update
      this.lastVideoTime = event.currentTime

      //// 1.4. Add buffering time to watch time (time ellaspsed between last waiting and playing or new action)
      if (this.lastWaitingEventReceived) {
        if (
          [
            ObserverMediaEventType.PLAYING,
            ObserverMediaEventType.PAUSE,
            ObserverMediaEventType.SEEKING,
            ObserverMediaEventType.ENDED,
            ObserverMediaEventType.EMPTIED,
            ObserverMediaEventType.LOAD_START,
          ].includes(event.type)
        ) {
          if (!event.paused) {
            this.watchTime +=
              (event.emittedAt - this.lastWaitingEventReceived.emittedAt) / 1000
          }
          this.lastWaitingEventReceived = null
        }
      }

      //// 1.5. Add watch time and default reason to event
      const modifiedEvent: BaseMediaEvent = {
        ...event,
        watchTime: this.watchTime,
        reason: SessionClosedEventReason.NONE,
        playbackId: this.playbackId,
        mediaId: this.mediaId,
      }

      //// 1.6. Keep waiting event to be able to compute the ellapsed time until next playing event
      if (event.type === ObserverMediaEventType.WAITING) {
        this.lastWaitingEventReceived = modifiedEvent
      }

      // ---- 2. HANDLE COMPUTED SESSION CLOSED EVENT (NOT A NATIVE PLAYER EVENT) ---- //

      //// 2.1. Keep Emptied event to be able compute a new Session Closed Event and not enqueue this event because it is not tracked event
      if (event.type === ObserverMediaEventType.EMPTIED) {
        this.lastEmptiedEventReceived = modifiedEvent
        return
      }

      //// 2.2. Keep Ended event to be able compute a new Session Closed Event
      if (event.type === ObserverMediaEventType.ENDED) {
        this.lastEndedEventReceived = modifiedEvent
      } else if (
        // or remove it if seeking in current media or loading new media instead of replay
        this.lastEndedEventReceived &&
        (event.type === ObserverMediaEventType.LOAD_START ||
          (event.type === ObserverMediaEventType.SEEKING &&
            event.currentTime !== 0))
      ) {
        this.lastEndedEventReceived = null
      }

      //// 2.3. Enqueue a new Session Closed event based on Ended event followed by Play event -> Completed with Replay reason
      if (this.lastEndedEventReceived) {
        if (event.type === ObserverMediaEventType.PLAY) {
          this.enqueueNewEvent({
            ...this.lastEndedEventReceived,
            emittedAt: event.emittedAt,
            type: ObserverMediaEventType.SESSION_CLOSED,
            reason: SessionClosedEventReason.REPLAY,
          })
          this.lastEndedEventReceived = null
          this.startNewPlayback()
        } else if (event.type !== ObserverMediaEventType.ENDED) {
          return
        }
      }

      //// 2.4. Enqueue a new Session Closed event based on Emptied event followed by Load Start event -> Completed with Src change reason
      if (
        this.lastEmptiedEventReceived &&
        event.type === ObserverMediaEventType.LOAD_START
      ) {
        this.enqueueNewEvent({
          ...this.lastEmptiedEventReceived,
          emittedAt: event.emittedAt,
          type: ObserverMediaEventType.SESSION_CLOSED,
          reason: SessionClosedEventReason.SRC_CHANGE,
        })
        this.lastEmptiedEventReceived = null
        this.startNewPlayback()
        // Apply waiting next media id
        if (this.nextMediaId) {
          this.mediaId = this.nextMediaId
          this.nextMediaId = null
        }
      }

      // ---- 3. ENQUEUE ANY EVENT (EXCEPT EMPTIED AND VISIBILITY_CHANGE) and add the reason if it's an exiting event (Desktop Session Closed event or Mobile Visibility Change to hidden)  ---- //
      const isDesktopExitingEvent =
        !this.isMobile && event.type === ObserverMediaEventType.SESSION_CLOSED

      const isMobileExitingEvent =
        this.isMobile && event.type === ObserverMediaEventType.VISIBILITY_CHANGE

      const isDesktopVisibilityEvent =
        event.type === ObserverMediaEventType.VISIBILITY_CHANGE &&
        !this.isMobile

      if (
        !(
          (event.type === ObserverMediaEventType.SESSION_CLOSED &&
            this.isMobile) ||
          isDesktopVisibilityEvent
        )
      ) {
        this.enqueueNewEvent({
          ...modifiedEvent,
          ...(isMobileExitingEvent && {
            type: ObserverMediaEventType.SESSION_CLOSED,
          }),
          ...((isDesktopExitingEvent || isMobileExitingEvent) && {
            reason: SessionClosedEventReason.EXIT,
          }),
          ...(modifiedEvent.playbackId !== this.playbackId && {
            playbackId: this.playbackId,
          }),
          ...(modifiedEvent.mediaId !== this.mediaId && {
            mediaId: this.mediaId,
          }),
        })
      }

      // ---- 4. FORCE TO IMMEDIATLY REPORT ENQUEUED EVENTS TO NOT LOSE IT BECAUSE PAGE HAS BEEN HIDDEN (TAB CLOSED OR VISIBILITY CHANGE) ---- //
      if (
        isDesktopExitingEvent ||
        isMobileExitingEvent ||
        isDesktopVisibilityEvent
      ) {
        this.report(this.queue.dequeue(), true)
        if (event.type === ObserverMediaEventType.VISIBILITY_CHANGE) {
          this.isHidden = true
        }
      }
    }
  }

  public enqueueNewEvent(event: BaseMediaEvent) {
    // Do not put event in queue if player is hidden on mobile
    if (this.isHidden && this.isMobile) {
      return
    }

    const inLastEvent = this.queue.peekLast()

    // If the last queued event is the same type with specific props (e.g. paused) as the upcoming one, the last queued
    // event is upserted. It avoids to queue too much events of the same types like "time_update".
    // NOTE: it does not prevent a repetitive sequence to be queued (e.g pause/play spam).
    if (
      inLastEvent &&
      inLastEvent.type === event.type &&
      inLastEvent.paused === event.paused
    ) {
      this.queue.upsertLast({
        ...event,
        sequenceNumber: inLastEvent.sequenceNumber,
      })
      return
    }

    this.queue.enqueue({ ...event, sequenceNumber: this.sequenceNumber })
    this.sequenceNumber++
  }

  private startNewPlayback(resetLastVideoTime = true) {
    if (resetLastVideoTime) {
      this.lastVideoTime = 0
    }
    this.watchTime = 0
    this.playbackId = uuidv7()
    this.sequenceNumber = initialSequenceNumber
  }

  public report(events: MediaEvent[], isExitingReport: boolean) {
    // Agent disabled or no event to send, exit.
    if (!this.options.enabled || events.length === 0) {
      return
    }

    postHttp(this.options.baseUrl, events, isExitingReport, () => {
      // The report failed: put the events back in the queue so they are sent
      // again with the next batch.
      this.queue.requeue(events)
    })
  }

  public dispose() {
    if (!this.options.enabled) return

    if (this.stopObserver) {
      this.stopObserver()
      this.stopObserver = null
    }

    if (this.stopTicker) {
      this.stopTicker()
      this.stopTicker = null
    }

    this.report(this.queue.dequeue(), false)

    this.observing = false
  }
}
